import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Download, Plus, Trash2 } from 'lucide-react';
import api from '../utils/api';
import { fmtDate, FY_OPTIONS, CURRENT_FY } from '../utils/helpers';
import { PageHeader, SearchBar, Modal, FormField } from '../components/Table';
import DueDateEditModal from '../components/DueDateEditModal';
import ClientSearchDropdown from '../components/ClientSearchDropdown';
import { useAuth } from '../context/AuthContext';
import { hasRole } from '../utils/helpers';
import toast from 'react-hot-toast';

const MODULE_COLORS = {
  'Income Tax': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', dot: 'bg-red-500' },
  'TDS':        { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', dot: 'bg-orange-500' },
  'GST':        { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', dot: 'bg-green-500' },
  'ROC':        { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', dot: 'bg-blue-500' },
  'Audit':      { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', dot: 'bg-purple-500' },
  'ESI/PF':     { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', dot: 'bg-yellow-500' },
};

// FY 2025-26 and FY 2026-27 statutory deadlines
const STATUTORY_DEADLINES = [
  // ── GST FY 2025-26 ──
  { title: 'GSTR-1 (Apr 2025)',  module: 'GST', due_date: '2025-05-11', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Apr 2025)', module: 'GST', due_date: '2025-05-20', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (May 2025)',  module: 'GST', due_date: '2025-06-11', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (May 2025)', module: 'GST', due_date: '2025-06-20', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Jun 2025)',  module: 'GST', due_date: '2025-07-11', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Jun 2025)', module: 'GST', due_date: '2025-07-20', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Jul 2025)',  module: 'GST', due_date: '2025-08-11', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Jul 2025)', module: 'GST', due_date: '2025-08-20', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Aug 2025)',  module: 'GST', due_date: '2025-09-11', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Aug 2025)', module: 'GST', due_date: '2025-09-20', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Sep 2025)',  module: 'GST', due_date: '2025-10-11', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Sep 2025)', module: 'GST', due_date: '2025-10-20', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Oct 2025)',  module: 'GST', due_date: '2025-11-11', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Oct 2025)', module: 'GST', due_date: '2025-11-20', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Nov 2025)',  module: 'GST', due_date: '2025-12-11', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Nov 2025)', module: 'GST', due_date: '2025-12-20', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-9 / 9C Annual (FY 2025-26)', module: 'GST', due_date: '2026-12-31', fy: '2025-26', client_name: 'All GST clients' },
  { title: 'GSTR-1 (Dec 2025)',  module: 'GST', due_date: '2026-01-11', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Dec 2025)', module: 'GST', due_date: '2026-01-20', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Jan 2026)',  module: 'GST', due_date: '2026-02-11', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Jan 2026)', module: 'GST', due_date: '2026-02-20', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Feb 2026)',  module: 'GST', due_date: '2026-03-11', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Feb 2026)', module: 'GST', due_date: '2026-03-20', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Mar 2026)',  module: 'GST', due_date: '2026-04-11', fy: '2025-26', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Mar 2026)', module: 'GST', due_date: '2026-04-20', fy: '2025-26', client_name: 'Monthly GST clients' },
  // ── GST FY 2026-27 ──
  { title: 'GSTR-1 (Apr 2026)',  module: 'GST', due_date: '2026-05-11', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Apr 2026)', module: 'GST', due_date: '2026-05-20', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (May 2026)',  module: 'GST', due_date: '2026-06-11', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (May 2026)', module: 'GST', due_date: '2026-06-20', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Jun 2026)',  module: 'GST', due_date: '2026-07-11', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Jun 2026)', module: 'GST', due_date: '2026-07-20', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Jul 2026)',  module: 'GST', due_date: '2026-08-11', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Jul 2026)', module: 'GST', due_date: '2026-08-20', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Aug 2026)',  module: 'GST', due_date: '2026-09-11', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Aug 2026)', module: 'GST', due_date: '2026-09-20', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Sep 2026)',  module: 'GST', due_date: '2026-10-11', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Sep 2026)', module: 'GST', due_date: '2026-10-20', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Oct 2026)',  module: 'GST', due_date: '2026-11-11', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Oct 2026)', module: 'GST', due_date: '2026-11-20', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Nov 2026)',  module: 'GST', due_date: '2026-12-11', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Nov 2026)', module: 'GST', due_date: '2026-12-20', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-9 / 9C Annual (FY 2026-27)', module: 'GST', due_date: '2027-12-31', fy: '2026-27', client_name: 'All GST clients' },
  { title: 'GSTR-1 (Dec 2026)',  module: 'GST', due_date: '2027-01-11', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Dec 2026)', module: 'GST', due_date: '2027-01-20', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Jan 2027)',  module: 'GST', due_date: '2027-02-11', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Jan 2027)', module: 'GST', due_date: '2027-02-20', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Feb 2027)',  module: 'GST', due_date: '2027-03-11', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Feb 2027)', module: 'GST', due_date: '2027-03-20', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-1 (Mar 2027)',  module: 'GST', due_date: '2027-04-11', fy: '2026-27', client_name: 'Monthly GST clients' },
  { title: 'GSTR-3B (Mar 2027)', module: 'GST', due_date: '2027-04-20', fy: '2026-27', client_name: 'Monthly GST clients' },
  // ── TDS FY 2025-26 ──
  { title: 'TDS Return Q1 — 24Q/26Q', module: 'TDS', due_date: '2025-07-31', fy: '2025-26', client_name: 'All TAN holders' },
  { title: 'TDS Return Q1 — 27EQ',    module: 'TDS', due_date: '2025-07-15', fy: '2025-26', client_name: 'TCS collectors' },
  { title: 'TDS Return Q2 — 24Q/26Q', module: 'TDS', due_date: '2025-10-31', fy: '2025-26', client_name: 'All TAN holders' },
  { title: 'TDS Return Q2 — 27EQ',    module: 'TDS', due_date: '2025-10-15', fy: '2025-26', client_name: 'TCS collectors' },
  { title: 'TDS Return Q3 — 24Q/26Q', module: 'TDS', due_date: '2026-01-31', fy: '2025-26', client_name: 'All TAN holders' },
  { title: 'TDS Return Q3 — 27EQ',    module: 'TDS', due_date: '2026-01-15', fy: '2025-26', client_name: 'TCS collectors' },
  { title: 'TDS Return Q4 — 24Q/26Q', module: 'TDS', due_date: '2026-05-31', fy: '2025-26', client_name: 'All TAN holders' },
  { title: 'TDS Return Q4 — 27EQ',    module: 'TDS', due_date: '2026-05-15', fy: '2025-26', client_name: 'TCS collectors' },
  // ── TDS FY 2026-27 ──
  { title: 'TDS Return Q1 — 24Q/26Q', module: 'TDS', due_date: '2026-07-31', fy: '2026-27', client_name: 'All TAN holders' },
  { title: 'TDS Return Q1 — 27EQ',    module: 'TDS', due_date: '2026-07-15', fy: '2026-27', client_name: 'TCS collectors' },
  { title: 'TDS Return Q2 — 24Q/26Q', module: 'TDS', due_date: '2026-10-31', fy: '2026-27', client_name: 'All TAN holders' },
  { title: 'TDS Return Q2 — 27EQ',    module: 'TDS', due_date: '2026-10-15', fy: '2026-27', client_name: 'TCS collectors' },
  { title: 'TDS Return Q3 — 24Q/26Q', module: 'TDS', due_date: '2027-01-31', fy: '2026-27', client_name: 'All TAN holders' },
  { title: 'TDS Return Q3 — 27EQ',    module: 'TDS', due_date: '2027-01-15', fy: '2026-27', client_name: 'TCS collectors' },
  { title: 'TDS Return Q4 — 24Q/26Q', module: 'TDS', due_date: '2027-05-31', fy: '2026-27', client_name: 'All TAN holders' },
  { title: 'TDS Return Q4 — 27EQ',    module: 'TDS', due_date: '2027-05-15', fy: '2026-27', client_name: 'TCS collectors' },
  // ── Income Tax FY 2025-26 (AY 2026-27) ──
  { title: 'Advance Tax Instalment 1 (FY 2025-26)', module: 'Income Tax', due_date: '2025-06-15', fy: '2025-26', client_name: 'All assessees' },
  { title: 'Advance Tax Instalment 2 (FY 2025-26)', module: 'Income Tax', due_date: '2025-09-15', fy: '2025-26', client_name: 'All assessees' },
  { title: 'Advance Tax Instalment 3 (FY 2025-26)', module: 'Income Tax', due_date: '2025-12-15', fy: '2025-26', client_name: 'All assessees' },
  { title: 'Advance Tax Instalment 4 (FY 2025-26)', module: 'Income Tax', due_date: '2026-03-15', fy: '2025-26', client_name: 'All assessees' },
  { title: 'Tax Audit Report (AY 2026-27)',          module: 'Income Tax', due_date: '2026-09-30', fy: '2025-26', client_name: 'Tax audit cases' },
  { title: 'ITR Filing Non-Audit (AY 2026-27)',      module: 'Income Tax', due_date: '2026-07-31', fy: '2025-26', client_name: 'Individual/HUF/Firm' },
  { title: 'ITR Filing Audit Cases (AY 2026-27)',    module: 'Income Tax', due_date: '2026-10-31', fy: '2025-26', client_name: 'Audit cases' },
  // ── Income Tax FY 2026-27 (AY 2027-28) ──
  { title: 'Advance Tax Instalment 1 (FY 2026-27)', module: 'Income Tax', due_date: '2026-06-15', fy: '2026-27', client_name: 'All assessees' },
  { title: 'Advance Tax Instalment 2 (FY 2026-27)', module: 'Income Tax', due_date: '2026-09-15', fy: '2026-27', client_name: 'All assessees' },
  { title: 'Advance Tax Instalment 3 (FY 2026-27)', module: 'Income Tax', due_date: '2026-12-15', fy: '2026-27', client_name: 'All assessees' },
  { title: 'Advance Tax Instalment 4 (FY 2026-27)', module: 'Income Tax', due_date: '2027-03-15', fy: '2026-27', client_name: 'All assessees' },
  { title: 'Tax Audit Report (AY 2027-28)',          module: 'Income Tax', due_date: '2027-09-30', fy: '2026-27', client_name: 'Tax audit cases' },
  { title: 'ITR Filing Non-Audit (AY 2027-28)',      module: 'Income Tax', due_date: '2027-07-31', fy: '2026-27', client_name: 'Individual/HUF/Firm' },
  { title: 'ITR Filing Audit Cases (AY 2027-28)',    module: 'Income Tax', due_date: '2027-10-31', fy: '2026-27', client_name: 'Audit cases' },
  // ── ROC FY 2025-26 ──
  { title: 'DPT-3',             module: 'ROC', due_date: '2026-06-30', fy: '2025-26', client_name: 'Companies' },
  { title: 'MSME-1 (Apr 2026)', module: 'ROC', due_date: '2026-04-30', fy: '2025-26', client_name: 'Companies' },
  { title: 'MSME-1 (Oct 2025)', module: 'ROC', due_date: '2025-10-31', fy: '2025-26', client_name: 'Companies' },
  { title: 'DIR-3 KYC',         module: 'ROC', due_date: '2026-09-30', fy: '2025-26', client_name: 'All directors' },
  { title: 'MGT-7 / MGT-7A',   module: 'ROC', due_date: '2026-11-29', fy: '2025-26', client_name: 'Companies (60d from AGM)' },
  { title: 'AOC-4',             module: 'ROC', due_date: '2026-10-29', fy: '2025-26', client_name: 'Companies (30d from AGM)' },
  // ── ROC FY 2026-27 ──
  { title: 'DPT-3',             module: 'ROC', due_date: '2027-06-30', fy: '2026-27', client_name: 'Companies' },
  { title: 'MSME-1 (Apr 2027)', module: 'ROC', due_date: '2027-04-30', fy: '2026-27', client_name: 'Companies' },
  { title: 'MSME-1 (Oct 2026)', module: 'ROC', due_date: '2026-10-31', fy: '2026-27', client_name: 'Companies' },
  { title: 'DIR-3 KYC',         module: 'ROC', due_date: '2027-09-30', fy: '2026-27', client_name: 'All directors' },
  // ── ESI/PF FY 2025-26 ──
  ...['2025-05','2025-06','2025-07','2025-08','2025-09','2025-10','2025-11','2025-12','2026-01','2026-02','2026-03'].map(m => ({
    title: `PF & ESI Payment (${m.replace('-', ' ')})`, module: 'ESI/PF', due_date: `${m}-15`, fy: '2025-26', client_name: 'All PF/ESI clients'
  })),
  // ── ESI/PF FY 2026-27 ──
  ...['2026-04','2026-05','2026-06','2026-07','2026-08','2026-09','2026-10','2026-11','2026-12','2027-01','2027-02','2027-03'].map(m => ({
    title: `PF & ESI Payment (${m.replace('-', ' ')})`, module: 'ESI/PF', due_date: `${m}-15`, fy: '2026-27', client_name: 'All PF/ESI clients'
  })),
];

const GOVT_EXT_MODULES = [
  { label: 'GST Returns',       module: 'GST',          table: 'gst_returns' },
  { label: 'TDS Returns',       module: 'TDS',          table: 'tds_returns' },
  { label: 'Income Tax (ITR)',  module: 'Income Tax',   table: 'itr_filings' },
  { label: 'ROC Filings',       module: 'ROC',          table: 'roc_compliance' },
  { label: 'Audit',             module: 'Audit',        table: 'audits' },
];

// ── Per-client record helpers ─────────────────────────────────────────────────

function moduleColor(module) {
  return MODULE_COLORS[module] || { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400' };
}

function dueDateClass(dateStr) {
  if (!dateStr) return '';
  const diff = (new Date(dateStr) - new Date()) / 86400000;
  if (diff < 0) return 'text-red-600 font-semibold';
  if (diff <= 7) return 'text-orange-600 font-semibold';
  return 'text-gray-700';
}

function exportCSV(rows, filename) {
  const header = 'Module,Description,Due Date,Status,Revised\n';
  const body = rows.map(r =>
    `"${r.module}","${r._desc}","${r.effective_due_date || r.due_date || ''}","${r.status || ''}","${r.revised_due_date ? 'Yes' : 'No'}"`
  ).join('\n');
  const blob = new Blob([header + body], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Govt Extension form (used in Bulk Extension tab) ─────────────────────────

function GovtExtForm({ onSuccess }) {
  const [form, setForm] = useState({ module: 'GST', table_name: 'gst_returns', return_type: '', period: '', original_due_date: '', extended_due_date: '', notification_ref: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.original_due_date || !form.extended_due_date) { toast.error('Both dates required'); return; }
    setSaving(true);
    try {
      const r = await api.post('/accounts/govt-extension', form);
      toast.success(r.data.message || 'Extension applied to all matching records');
      onSuccess?.();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-700">
        Updates <strong>all clients</strong> with matching due date. Use for CBDT / GSTN / MCA circulars that extend a deadline for everyone.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Module">
          <select value={form.module} onChange={e => { const m = GOVT_EXT_MODULES.find(x => x.module === e.target.value); f('module', e.target.value); if (m) f('table_name', m.table); }} className="input-field">
            {GOVT_EXT_MODULES.map(m => <option key={m.module} value={m.module}>{m.label}</option>)}
          </select>
        </FormField>
        <FormField label="Return / Form Type">
          <input value={form.return_type} onChange={e => f('return_type', e.target.value)} className="input-field" placeholder="e.g. GSTR-3B, 26Q" />
        </FormField>
        <FormField label="Period (optional)">
          <input value={form.period} onChange={e => f('period', e.target.value)} className="input-field" placeholder="e.g. Q1, Apr 2025" />
        </FormField>
        <FormField label="Notification Ref">
          <input value={form.notification_ref} onChange={e => f('notification_ref', e.target.value)} className="input-field" placeholder="Circular No." />
        </FormField>
        <FormField label="Original Due Date" required>
          <input type="date" value={form.original_due_date} onChange={e => f('original_due_date', e.target.value)} className="input-field" />
        </FormField>
        <FormField label="Extended Due Date" required>
          <input type="date" value={form.extended_due_date} onChange={e => f('extended_due_date', e.target.value)} className="input-field" />
        </FormField>
      </div>
      <FormField label="Notes">
        <textarea value={form.notes} onChange={e => f('notes', e.target.value)} className="input-field" rows={2} />
      </FormField>
      <button onClick={save} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50">
        {saving ? 'Applying…' : 'Apply to ALL Clients'}
      </button>
    </div>
  );
}

// ── Bulk per-client extension form ────────────────────────────────────────────

function BulkClientForm() {
  const [form, setForm] = useState({ table_name: 'gst_returns', return_type: '', period: '', original_due_date: '', extended_due_date: '', reason_type: 'Govt Extension', notes: '' });
  const [clients, setClients] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('/clients', { params: { status: 'Active' } }).then(r => setClients(r.data));
  }, []);

  const displayClients = clients.filter(c => !clientSearch || c.name.toLowerCase().includes(clientSearch.toLowerCase()) || (c.client_code || '').toLowerCase().includes(clientSearch.toLowerCase()));
  const toggleClient = id => setSelectedIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll = () => setSelectedIds(selectedIds.length === displayClients.length ? [] : displayClients.map(c => c.id));

  const save = async () => {
    if (!form.original_due_date || !form.extended_due_date) { toast.error('Both dates required'); return; }
    if (!selectedIds.length) { toast.error('Select at least one client'); return; }
    setSaving(true);
    try {
      const r = await api.post('/accounts/bulk-client-extension', { ...form, client_ids: selectedIds });
      toast.success(r.data.message);
      setSelectedIds([]);
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const REASON_TYPES = ['Govt Extension', 'Client Request', 'Internal', 'Court Order'];

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
        Apply an extension to <strong>specific selected clients only</strong>. Does not affect other clients.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Module / Table">
          <select value={form.table_name} onChange={e => f('table_name', e.target.value)} className="input-field">
            {GOVT_EXT_MODULES.map(m => <option key={m.table} value={m.table}>{m.label}</option>)}
          </select>
        </FormField>
        <FormField label="Return / Form Type">
          <input value={form.return_type} onChange={e => f('return_type', e.target.value)} className="input-field" placeholder="e.g. GSTR-3B, 26Q" />
        </FormField>
        <FormField label="Original Due Date" required>
          <input type="date" value={form.original_due_date} onChange={e => f('original_due_date', e.target.value)} className="input-field" />
        </FormField>
        <FormField label="Extended Due Date" required>
          <input type="date" value={form.extended_due_date} onChange={e => f('extended_due_date', e.target.value)} className="input-field" />
        </FormField>
        <FormField label="Reason">
          <select value={form.reason_type} onChange={e => f('reason_type', e.target.value)} className="input-field">
            {REASON_TYPES.map(r => <option key={r}>{r}</option>)}
          </select>
        </FormField>
        <FormField label="Notes">
          <input value={form.notes} onChange={e => f('notes', e.target.value)} className="input-field" />
        </FormField>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Select Clients ({selectedIds.length} selected)</span>
          <button onClick={toggleAll} className="text-xs text-[#1e3a5f] hover:underline">{selectedIds.length === displayClients.length ? 'Deselect All' : 'Select All'}</button>
        </div>
        <input value={clientSearch} onChange={e => setClientSearch(e.target.value)} className="input-field mb-2" placeholder="Search clients…" />
        <div className="border rounded-lg max-h-48 overflow-y-auto divide-y">
          {displayClients.map(c => (
            <label key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={selectedIds.includes(c.id)} onChange={() => toggleClient(c.id)} className="rounded" />
              <span className="text-sm">{c.client_code && <span className="text-xs text-gray-400 mr-1">{c.client_code}</span>}{c.name}</span>
            </label>
          ))}
          {!displayClients.length && <p className="text-sm text-gray-400 p-3 text-center">No clients found</p>}
        </div>
      </div>

      <button onClick={save} disabled={saving || !selectedIds.length} className="w-full btn-primary disabled:opacity-50">
        {saving ? 'Applying…' : `Apply to ${selectedIds.length} Selected Client(s)`}
      </button>
    </div>
  );
}

// ── Per-Client dues view ──────────────────────────────────────────────────────

function PerClientDues() {
  const [clientId, setClientId] = useState('');
  const [clientLabel, setClientLabel] = useState('');
  const [dues, setDues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dueDateRecord, setDueDateRecord] = useState(null);
  const [moduleFilter, setModuleFilter] = useState('');

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const [itr, tds, gst, audit, roc] = await Promise.all([
        api.get('/income-tax/itr',    { params: { client_id: id } }),
        api.get('/tds/returns',       { params: { client_id: id } }),
        api.get('/gst/returns',       { params: { client_id: id } }),
        api.get('/audit',             { params: { client_id: id } }),
        api.get('/roc/compliance',    { params: { client_id: id } }),
      ]);
      const all = [
        ...itr.data.map(r  => ({ ...r, _module: 'Income Tax', _tableName: 'itr_filings',   _desc: `ITR ${r.ay}` })),
        ...tds.data.map(r  => ({ ...r, _module: 'TDS',        _tableName: 'tds_returns',    _desc: `${r.form_type} ${r.quarter} ${r.fy}` })),
        ...gst.data.map(r  => ({ ...r, _module: 'GST',        _tableName: 'gst_returns',    _desc: `${r.return_type} ${r.period}` })),
        ...audit.data.map(r => ({ ...r, _module: 'Audit',     _tableName: 'audits',         _desc: `${r.audit_type} ${r.fy}` })),
        ...roc.data.map(r  => ({ ...r, _module: 'ROC',        _tableName: 'roc_compliance', _desc: `${r.form_type} ${r.fy}` })),
      ].sort((a, b) => ((a.effective_due_date || a.due_date) || '').localeCompare((b.effective_due_date || b.due_date) || ''));
      setDues(all);
    } finally { setLoading(false); }
  }, []);

  const handleClientChange = (id, label) => {
    setClientId(id); setClientLabel(label); setDues([]);
    if (id) load(id);
  };

  const filtered = dues.filter(d => !moduleFilter || d._module === moduleFilter);
  const modules = [...new Set(dues.map(d => d._module))];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-64">
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Client</label>
          <ClientSearchDropdown
            value={clientId}
            initialLabel={clientLabel}
            onChange={handleClientChange}
            placeholder="Type 2+ characters to search…"
          />
        </div>
        {clientId && (
          <button onClick={() => exportCSV(filtered, `due-dates-${clientLabel}.csv`)} className="flex items-center gap-1.5 text-sm px-3 py-2 border rounded-lg hover:bg-gray-50 text-gray-600">
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>

      {clientId && modules.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setModuleFilter('')} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${!moduleFilter ? 'bg-[#1e3a5f] text-white border-transparent' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>All</button>
          {modules.map(m => {
            const c = moduleColor(m);
            return (
              <button key={m} onClick={() => setModuleFilter(moduleFilter === m ? '' : m)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${moduleFilter === m ? `${c.bg} ${c.text} border-current` : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                <span className={`w-2 h-2 rounded-full ${c.dot}`} />{m}
              </button>
            );
          })}
        </div>
      )}

      {loading && <div className="text-center py-8 text-gray-400">Loading compliance records…</div>}

      {!clientId && !loading && (
        <div className="text-center py-12 text-gray-400 text-sm">Search for a client above to view their due dates</div>
      )}

      {clientId && !loading && filtered.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm">No compliance records found for this client</div>
      )}

      {filtered.length > 0 && (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Module</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Description</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Due Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((row, i) => {
                const effDate = row.effective_due_date || row.due_date;
                const colors = moduleColor(row._module);
                return (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors.bg} ${colors.text}`}>{row._module}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-800">{row._desc}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className={dueDateClass(effDate)}>{fmtDate(effDate) || '—'}</span>
                        {row.govt_extended ? <span className="bg-orange-100 text-orange-600 text-xs px-1.5 py-0.5 rounded font-medium">Govt Ext</span> : null}
                        {row.revised_due_date && !row.govt_extended ? <span className="bg-blue-100 text-blue-600 text-xs px-1.5 py-0.5 rounded font-medium">Revised</span> : null}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">{row.status || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setDueDateRecord({ ...row, tableName: row._tableName })} className="text-gray-300 hover:text-[#f0a500] transition-colors" title="Edit due date">
                        <Pencil size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {dueDateRecord && (
        <DueDateEditModal
          record={dueDateRecord}
          tableName={dueDateRecord.tableName}
          moduleName={dueDateRecord._module}
          onClose={() => setDueDateRecord(null)}
          onSaved={() => { setDueDateRecord(null); load(clientId); }}
        />
      )}
    </div>
  );
}

// ── Statutory calendar item ───────────────────────────────────────────────────

function DeadlineItem({ d }) {
  const colors = moduleColor(d.module);
  const diff = Math.ceil((new Date(d.due_date) - new Date()) / 86400000);
  const isOverdue = diff < 0;
  const isToday = diff === 0;
  const isSoon = diff <= 7 && diff >= 0;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${isOverdue ? 'bg-red-50 border-red-200' : isToday ? 'bg-orange-50 border-orange-200' : isSoon ? 'bg-yellow-50 border-yellow-100' : 'bg-white border-gray-100'} hover:shadow-sm transition-shadow`}>
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${isOverdue ? 'text-red-700' : 'text-gray-800'}`}>{d.title}</div>
        <div className="text-xs text-gray-400">{d.client_name}</div>
      </div>
      <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>{d.module}</span>
      <div className={`text-xs font-semibold w-24 text-right flex-shrink-0 ${isOverdue ? 'text-red-600' : isToday ? 'text-orange-600' : isSoon ? 'text-yellow-600' : 'text-gray-500'}`}>
        {isOverdue ? `${Math.abs(diff)}d overdue` : isToday ? 'TODAY' : fmtDate(d.due_date)}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const MODULES_LIST = ['GST', 'TDS', 'Income Tax', 'ROC', 'Audit', 'ESI/PF', 'General'];

function CustomDueDateModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', module: 'General', due_date: '', apply_to: 'all', fy: '2025-26', repeat_type: 'one-time', notes: '' });
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.title || !form.due_date) { toast.error('Title and due date are required'); return; }
    setSaving(true);
    try {
      await api.post('/accounts/custom-due-dates', form);
      toast.success('Custom due date added');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Add Custom Due Date" onClose={onClose}>
      <div className="space-y-3">
        <FormField label="Title" required>
          <input value={form.title} onChange={e => f('title', e.target.value)} className="input-field" placeholder="e.g. Client ABC Board Meeting" />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Module">
            <select value={form.module} onChange={e => f('module', e.target.value)} className="input-field">
              {MODULES_LIST.map(m => <option key={m}>{m}</option>)}
            </select>
          </FormField>
          <FormField label="Due Date" required>
            <input type="date" value={form.due_date} onChange={e => f('due_date', e.target.value)} className="input-field" />
          </FormField>
          <FormField label="FY">
            <select value={form.fy} onChange={e => f('fy', e.target.value)} className="input-field">
              <option value="2025-26">FY 2025-26</option>
              <option value="2026-27">FY 2026-27</option>
              <option value="">No specific FY</option>
            </select>
          </FormField>
          <FormField label="Repeat">
            <select value={form.repeat_type} onChange={e => f('repeat_type', e.target.value)} className="input-field">
              <option value="one-time">One-time</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </FormField>
        </div>
        <FormField label="Notes">
          <textarea value={form.notes} onChange={e => f('notes', e.target.value)} className="input-field" rows={2} placeholder="Optional notes…" />
        </FormField>
        <div className="flex gap-3">
          <button onClick={save} disabled={saving} className="btn-primary flex-1">{saving ? 'Saving…' : 'Save'}</button>
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

// ── Due Dates Master ────────────────────────────────────────────────────────────
// FY selector → category tabs → every return type with its statutory due date.
// Edits write FY-scoped overrides to the same settings keys the recurring
// engine reads, so an extension here changes real due-date generation.
const MASTER_CATEGORIES = ['Income Tax', 'TDS', 'GST', 'ROC', 'ESI/PF', 'Audit'];

function fmtDueValue(row, value) {
  if (row.type === 'day') return `Day ${value} ${row.note ? `(${row.note})` : ''}`;
  const [mm, dd] = String(value).split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const m = months[parseInt(mm, 10) - 1] || mm;
  return `${parseInt(dd, 10)} ${m} ${row.note ? `(${row.note})` : ''}`;
}

function DueDatesMaster({ isAdmin }) {
  const [fy, setFy] = useState(CURRENT_FY);
  const [cat, setCat] = useState('Income Tax');
  const [rows, setRows] = useState([]);
  const [editKey, setEditKey] = useState(null);
  const [editVal, setEditVal] = useState('');

  const load = useCallback(() => {
    api.get(`/settings/due-dates/${fy}`).then(r => setRows(r.data)).catch(() => {});
  }, [fy]);
  useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    try {
      await api.put(`/settings/due-dates/${fy}/${key}`, { value: editVal });
      toast.success(editVal ? `Extension saved for FY ${fy}` : 'Override cleared');
      setEditKey(null); setEditVal(''); load();
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };

  const catRows = rows.filter(r => r.module === cat);

  return (
    <div className="space-y-4">
      {/* FY selector at the very top */}
      <div className="card flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Financial Year</span>
        <select value={fy} onChange={e => setFy(e.target.value)} className="input-field w-36">
          {FY_OPTIONS.map(f => <option key={f}>{f}</option>)}
        </select>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Overrides saved here apply to FY {fy} only and feed the auto-created records' due dates.
        </span>
      </div>

      {/* Category tabs */}
      <div className="flex gap-1 flex-wrap">
        {MASTER_CATEGORIES.map(c => (
          <button key={c} onClick={() => { setCat(c); setEditKey(null); }}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${cat === c ? 'bg-[#1e3a5f] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
            {c}
          </button>
        ))}
      </div>

      {/* Return types for selected category + FY */}
      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="table-header text-left">Return / Filing</th>
              <th className="table-header text-left">Due Date (FY {fy})</th>
              <th className="table-header text-left">Source</th>
              {isAdmin && <th className="table-header text-left">Edit (Govt Extension)</th>}
            </tr>
          </thead>
          <tbody>
            {catRows.map(r => (
              <tr key={r.key} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="table-cell font-medium">{r.label}</td>
                <td className="table-cell">
                  <span className={r.fy_override ? 'text-orange-600 font-semibold' : ''}>{fmtDueValue(r, r.effective)}</span>
                </td>
                <td className="table-cell">
                  {r.fy_override
                    ? <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Extended (FY {fy})</span>
                    : r.base_override
                      ? <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Custom (all FYs)</span>
                      : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Statutory</span>}
                </td>
                {isAdmin && (
                  <td className="table-cell">
                    {editKey === r.key ? (
                      <div className="flex items-center gap-2">
                        <input value={editVal} onChange={e => setEditVal(e.target.value)}
                          placeholder={r.type === 'day' ? 'e.g. 13 (day of month)' : 'MM-DD e.g. 08-31'}
                          className="input-field w-40 text-xs py-1" autoFocus />
                        <button onClick={() => save(r.key)} className="text-xs btn-primary py-1 px-3">Save</button>
                        <button onClick={() => { setEditKey(null); setEditVal(''); }} className="text-xs text-gray-500">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <button onClick={() => { setEditKey(r.key); setEditVal(r.fy_override || ''); }}
                          className="text-xs text-[#1e3a5f] hover:text-[#f0a500] flex items-center gap-1">
                          <Pencil size={12} /> Edit
                        </button>
                        {r.fy_override && (
                          <button onClick={() => { setEditKey(null); setEditVal(''); api.put(`/settings/due-dates/${fy}/${r.key}`, { value: null }).then(() => { toast.success('Override cleared'); load(); }); }}
                            className="text-xs text-red-400 hover:text-red-600">Clear</button>
                        )}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('master');
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [viewFilter, setViewFilter] = useState('upcoming');
  const [fyFilter, setFyFilter] = useState('2025-26');
  const [customDates, setCustomDates] = useState([]);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const isAdmin = hasRole(user, 'partner', 'senior_staff');

  const loadCustomDates = useCallback(() => {
    api.get('/accounts/custom-due-dates').then(r => setCustomDates(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadCustomDates(); }, []);

  const deleteCustomDate = async (id) => {
    if (!window.confirm('Delete this custom due date?')) return;
    try { await api.delete(`/accounts/custom-due-dates/${id}`); loadCustomDates(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const today = new Date().toISOString().split('T')[0];
  const thisMonthStart = today.slice(0, 7) + '-01';
  const thisMonthEnd = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0];
  const nextMonthStart = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString().split('T')[0];
  const nextMonthEnd = new Date(new Date().getFullYear(), new Date().getMonth() + 2, 0).toISOString().split('T')[0];

  const customForFY = customDates
    .filter(d => !d.fy || d.fy === fyFilter)
    .map(d => ({ ...d, _custom: true, client_name: d.apply_to === 'all' ? 'All clients' : (d.client_name || 'Specific client') }));

  const baseDates = [
    ...STATUTORY_DEADLINES.filter(d => d.fy === fyFilter),
    ...customForFY,
  ];

  const filtered = baseDates.filter(d => {
    const matchSearch = !search || d.title.toLowerCase().includes(search.toLowerCase());
    const matchModule = !moduleFilter || d.module === moduleFilter;
    if (!matchSearch || !matchModule) return false;
    if (viewFilter === 'overdue')     return d.due_date < today;
    if (viewFilter === 'this-month')  return d.due_date >= thisMonthStart && d.due_date <= thisMonthEnd;
    if (viewFilter === 'next-month')  return d.due_date >= nextMonthStart && d.due_date <= nextMonthEnd;
    if (viewFilter === 'upcoming')    return d.due_date >= today;
    return true; // 'all'
  }).sort((a, b) => a.due_date.localeCompare(b.due_date));

  const VIEW_FILTERS = [
    { key: 'upcoming',   label: 'Upcoming' },
    { key: 'this-month', label: 'This Month' },
    { key: 'next-month', label: 'Next Month' },
    { key: 'overdue',    label: 'Overdue' },
    { key: 'all',        label: 'All' },
  ];

  const exportCalendarCSV = () => {
    const header = 'FY,Module,Title,Due Date,Applicable To\n';
    const body = filtered.map(d => `"${d.fy}","${d.module}","${d.title}","${d.due_date}","${d.client_name}"`).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `statutory-calendar-${fyFilter}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const TABS = [
    { key: 'master',        label: '📘 Due Dates Master' },
    { key: 'calendar',      label: '📅 Statutory Calendar' },
    { key: 'per-client',    label: '👤 Per-Client Dues' },
    ...(isAdmin ? [{ key: 'bulk-ext', label: '🏛️ Bulk Extension' }] : []),
  ];

  return (
    <div>
      <PageHeader title="Due Date Management" subtitle="Statutory calendar, per-client dues & bulk extensions" />

      {/* Tabs */}
      <div className="flex gap-1 mb-5">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === t.key ? 'bg-[#1e3a5f] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TAB: Due Dates Master ── */}
      {tab === 'master' && <DueDatesMaster isAdmin={isAdmin} />}

      {/* ── TAB: Statutory Calendar ── */}
      {tab === 'calendar' && (
        <div>
          {/* Legend */}
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(MODULE_COLORS).map(([m, c]) => (
              <button key={m} onClick={() => setModuleFilter(moduleFilter === m ? '' : m)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${moduleFilter === m ? `${c.bg} ${c.text} border-current` : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                <span className={`w-2 h-2 rounded-full ${c.dot}`} />{m}
              </button>
            ))}
            {moduleFilter && <button onClick={() => setModuleFilter('')} className="text-xs text-gray-400 hover:text-red-500 px-2">Clear ×</button>}
          </div>

          {/* Filters row */}
          <div className="flex flex-wrap gap-3 mb-4">
            <SearchBar value={search} onChange={setSearch} placeholder="Search deadlines…" />
            <select value={fyFilter} onChange={e => setFyFilter(e.target.value)} className="input-field w-32">
              <option value="2025-26">FY 2025-26</option>
              <option value="2026-27">FY 2026-27</option>
            </select>
            <div className="flex gap-1 flex-wrap">
              {VIEW_FILTERS.map(vf => (
                <button key={vf.key} onClick={() => setViewFilter(vf.key)}
                  className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${viewFilter === vf.key ? 'bg-[#1e3a5f] text-white' : 'text-gray-600 border hover:border-gray-400'}`}>
                  {vf.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 ml-auto">
              {isAdmin && (
                <button onClick={() => setShowCustomModal(true)} className="flex items-center gap-1.5 text-sm px-3 py-2 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#162d4a]">
                  <Plus size={14} /> Add Custom Due Date
                </button>
              )}
              <button onClick={exportCalendarCSV} className="flex items-center gap-1.5 text-sm px-3 py-2 border rounded-lg hover:bg-gray-50 text-gray-600">
                <Download size={14} /> Export CSV
              </button>
            </div>
          </div>

          {/* Color coding legend */}
          <div className="flex flex-wrap gap-3 mb-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" /> Overdue</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-400 inline-block" /> Due today</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block" /> Due within 7 days</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-300 inline-block" /> Upcoming</span>
          </div>

          {customForFY.length > 0 && (
            <div className="text-xs text-gray-400 mb-2">{customForFY.length} custom due date{customForFY.length > 1 ? 's' : ''} added</div>
          )}
          {filtered.length === 0
            ? <div className="text-center py-12 text-gray-400">No deadlines match the current filters</div>
            : <div className="space-y-2">{filtered.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex-1"><DeadlineItem d={d} /></div>
                  {d._custom && isAdmin && (
                    <button onClick={() => deleteCustomDate(d.id)} className="text-gray-300 hover:text-red-500 p-1 flex-shrink-0" title="Delete custom entry">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              ))}</div>
          }
        </div>
      )}

      {showCustomModal && (
        <CustomDueDateModal onClose={() => setShowCustomModal(false)} onSaved={() => { loadCustomDates(); setShowCustomModal(false); }} />
      )}

      {/* ── TAB: Per-Client ── */}
      {tab === 'per-client' && <PerClientDues />}

      {/* ── TAB: Bulk Extension (admin) ── */}
      {tab === 'bulk-ext' && isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <h3 className="font-semibold text-gray-800 mb-4">Global Extension — All Clients</h3>
            <GovtExtForm />
          </div>
          <div className="card">
            <h3 className="font-semibold text-gray-800 mb-4">Per-Client Extension — Select Clients</h3>
            <BulkClientForm />
          </div>
        </div>
      )}
    </div>
  );
}
